
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, XCircle, TrendingUp, Shield, DollarSign, Clock, Users, Wrench, AlertTriangle, Home } from "lucide-react";
import Navigation from "@/components/Navigation";

const ContractsVsRental = () => {
  const contractAdvantages = [
    {
      icon: <DollarSign className="h-6 w-6 text-green-600" />,
      title: "Higher Returns",
      description: "8-15% annual yields vs 4-8% for rental properties",
      highlight: true
    },
    {
      icon: <Clock className="h-6 w-6 text-blue-600" />,
      title: "Zero Management",
      description: "No tenant management, maintenance, or property management hassles",
      highlight: true
    },
    {
      icon: <Shield className="h-6 w-6 text-purple-600" />,
      title: "Asset-Backed Security",
      description: "Secured by real estate with clear title and equity position",
      highlight: true
    },
    {
      icon: <TrendingUp className="h-6 w-6 text-orange-600" />,
      title: "Predictable Cash Flow",
      description: "Fixed monthly payments with no vacancy risk",
      highlight: true
    },
    {
      icon: <Users className="h-6 w-6 text-indigo-600" />,
      title: "Motivated Buyers",
      description: "Buyers are invested homeowners, not tenants",
      highlight: true
    },
    {
      icon: <Home className="h-6 w-6 text-green-600" />,
      title: "Lower Entry Cost",
      description: "Start with $15,000 vs $50,000+ for rental properties"
    }
  ];

  const comparisonData = [
    {
      category: "Expected Annual Return",
      contracts: "8-15%",
      rental: "4-8%",
      winner: "contracts"
    },
    {
      category: "Management Required",
      contracts: "None",
      rental: "Significant",
      winner: "contracts"
    },
    {
      category: "Tenant/Vacancy Risk",
      contracts: "None",
      rental: "High",
      winner: "contracts"
    },
    {
      category: "Maintenance Responsibility",
      contracts: "None",
      rental: "Full Responsibility",
      winner: "contracts"
    },
    {
      category: "Cash Flow Predictability",
      contracts: "Fixed Monthly",
      rental: "Variable",
      winner: "contracts"
    },
    {
      category: "Property Insurance",
      contracts: "Buyer Pays",
      rental: "Investor Pays",
      winner: "contracts"
    },
    {
      category: "Property Taxes",
      contracts: "Buyer Pays",
      rental: "Investor Pays",
      winner: "contracts"
    },
    {
      category: "Minimum Investment",
      contracts: "$15,000+",
      rental: "$50,000+",
      winner: "contracts"
    },
    {
      category: "Liquidity",
      contracts: "Moderate",
      rental: "Low",
      winner: "contracts"
    },
    {
      category: "Geographic Flexibility",
      contracts: "Nationwide",
      rental: "Local Markets",
      winner: "contracts"
    },
    {
      category: "Time Investment",
      contracts: "Minimal",
      rental: "Substantial",
      winner: "contracts"
    },
    {
      category: "Depreciation Benefits",
      contracts: "Limited",
      rental: "Full Benefits",
      winner: "rental"
    }
  ];

  const contractBenefits = [
    "No late-night emergency calls",
    "No tenant screening or evictions",
    "No property maintenance or repairs",
    "No vacancy periods between tenants",
    "No property management fees",
    "No dealing with problem tenants",
    "No HOA or municipal violations",
    "No property insurance claims",
    "No seasonal maintenance requirements",
    "No capital expenditure surprises"
  ];

  const rentalChallenges = [
    "Tenant turnover and vacancy costs",
    "Maintenance and repair expenses",
    "Property management time/fees",
    "Dealing with difficult tenants",
    "Emergency repairs and calls",
    "Property insurance and taxes",
    "Local regulations and compliance",
    "Market rent fluctuations",
    "Eviction processes and costs",
    "Capital improvements needed"
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <Navigation />
      
      <div className="py-8 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Contracts VS Rental Properties
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A comprehensive comparison of land contract investments versus traditional rental property investments. 
              Discover why land contracts offer superior returns with zero management hassles.
            </p>
          </div>

          {/* Key Advantages Section */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-center text-gray-900 mb-8">
              Why Land Contracts Win
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {contractAdvantages.map((advantage, index) => (
                <Card key={index} className={`hover:shadow-lg transition-shadow ${advantage.highlight ? 'border-green-200 bg-green-50' : ''}`}>
                  <CardHeader>
                    <div className="flex items-center space-x-3">
                      {advantage.icon}
                      <CardTitle className="text-lg">{advantage.title}</CardTitle>
                      {advantage.highlight && (
                        <Badge variant="secondary" className="bg-green-100 text-green-800">
                          Major Advantage
                        </Badge>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600">{advantage.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Side-by-Side Comparison Table */}
          <Card className="mb-16">
            <CardHeader>
              <CardTitle className="text-2xl text-center">Head-to-Head Comparison</CardTitle>
              <CardDescription className="text-center text-lg">
                See how land contracts stack up against rental properties across key investment criteria
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b-2 border-gray-200">
                      <th className="text-left p-4 font-semibold text-gray-900">Investment Criteria</th>
                      <th className="text-center p-4 font-semibold text-green-700 bg-green-50">Land Contracts</th>
                      <th className="text-center p-4 font-semibold text-blue-700 bg-blue-50">Rental Properties</th>
                      <th className="text-center p-4 font-semibold text-gray-700">Winner</th>
                    </tr>
                  </thead>
                  <tbody>
                    {comparisonData.map((row, index) => (
                      <tr key={index} className={index % 2 === 0 ? "bg-gray-50" : "bg-white"}>
                        <td className="p-4 font-medium text-gray-900">{row.category}</td>
                        <td className={`p-4 text-center ${row.winner === 'contracts' ? 'font-semibold text-green-700 bg-green-50' : 'text-gray-700'}`}>
                          {row.contracts}
                        </td>
                        <td className={`p-4 text-center ${row.winner === 'rental' ? 'font-semibold text-blue-700 bg-blue-50' : 'text-gray-700'}`}>
                          {row.rental}
                        </td>
                        <td className="p-4 text-center">
                          {row.winner === 'contracts' ? (
                            <CheckCircle className="h-5 w-5 text-green-600 mx-auto" />
                          ) : (
                            <CheckCircle className="h-5 w-5 text-blue-600 mx-auto" />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Benefits vs Challenges */}
          <div className="grid md:grid-cols-2 gap-8 mb-16">
            <Card className="border-green-200">
              <CardHeader className="bg-green-50">
                <CardTitle className="text-xl text-green-800 flex items-center">
                  <CheckCircle className="h-6 w-6 mr-2" />
                  Land Contract Benefits
                </CardTitle>
                <CardDescription>
                  What you get with land contract investments
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                <ul className="space-y-3">
                  {contractBenefits.map((benefit, index) => (
                    <li key={index} className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            <Card className="border-red-200">
              <CardHeader className="bg-red-50">
                <CardTitle className="text-xl text-red-800 flex items-center">
                  <AlertTriangle className="h-6 w-6 mr-2" />
                  Rental Property Challenges
                </CardTitle>
                <CardDescription>
                  Common issues with rental property investments
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                <ul className="space-y-3">
                  {rentalChallenges.map((challenge, index) => (
                    <li key={index} className="flex items-start">
                      <XCircle className="h-5 w-5 text-red-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{challenge}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>

          {/* Bottom CTA */}
          <Card className="bg-gradient-to-r from-green-50 to-blue-50 border-green-200">
            <CardHeader className="text-center">
              <CardTitle className="text-2xl text-gray-900">The Clear Winner</CardTitle>
              <CardDescription className="text-lg">
                Land contracts offer superior returns with zero management hassles
              </CardDescription>
            </CardHeader>
            <CardContent className="text-center">
              <div className="grid md:grid-cols-3 gap-6 mb-8">
                <div className="flex flex-col items-center">
                  <TrendingUp className="h-16 w-16 text-green-600 mb-3" />
                  <h4 className="font-semibold text-gray-900 mb-2">Higher Returns</h4>
                  <p className="text-gray-600">8-15% vs 4-8% for rentals</p>
                </div>
                <div className="flex flex-col items-center">
                  <Clock className="h-16 w-16 text-blue-600 mb-3" />
                  <h4 className="font-semibold text-gray-900 mb-2">Zero Time Investment</h4>
                  <p className="text-gray-600">Passive income without management</p>
                </div>
                <div className="flex flex-col items-center">
                  <Shield className="h-16 w-16 text-purple-600 mb-3" />
                  <h4 className="font-semibold text-gray-900 mb-2">Asset-Backed Security</h4>
                  <p className="text-gray-600">Real estate collateral protection</p>
                </div>
              </div>
              <div className="space-y-4">
                <p className="text-xl font-semibold text-gray-900">
                  Ready to experience the land contract advantage?
                </p>
                <p className="text-gray-600">
                  Discover vetted investment opportunities that deliver superior returns without the landlord headaches
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ContractsVsRental;
